import React, { useEffect, useId, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

interface ModalProps { open: boolean; onClose: () => void; title?: string; ariaLabel?: string; closeLabel?: string; children: React.ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'; className?: string; viewportClassName?: string; }
const sizeMap = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl', full: 'max-w-full mx-4' };

export const Modal: React.FC<ModalProps> = ({ open, onClose, title, ariaLabel, closeLabel = 'Close', children, size = 'md', className = '', viewportClassName = '' }) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement;
    const panel = panelRef.current;
    const focusable = () => Array.from(panel?.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])') ?? []);
    focusable()[0]?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); previousFocus.current?.focus(); };
  }, [open, onClose]);

  return <AnimatePresence>{open && <motion.div className={`modal-viewport fixed inset-0 z-50 flex items-center justify-center p-4 ${viewportClassName}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
    <button type="button" className="absolute inset-0 bg-navy-950/80 backdrop-blur-sm" onClick={onClose} aria-label={closeLabel} tabIndex={-1} />
    <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined} aria-label={title ? undefined : (ariaLabel ?? 'Dialog')} className={`modal-panel relative w-full ${sizeMap[size]} glass-card rounded-2xl p-6 ${className}`} initial={{ scale: .96, opacity: 0, y: 14 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .96, opacity: 0, y: 14 }}>
      {title && <div className="mb-5 flex items-center justify-between gap-3"><h2 id={titleId} className="text-lg font-bold text-champagne-100">{title}</h2><button type="button" onClick={onClose} className="icon-control" aria-label={closeLabel}><X size={18} /></button></div>}
      {children}
    </motion.div>
  </motion.div>}</AnimatePresence>;
};
